import { Outlet, useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import defaultContent from "../../../content/actionReportsWhosNew";
import "./WhosNewActionReportLayout.scss";
import { ActionReportHeader } from "../common/Header/ActionReportHeader";
import { ActionReportToolbar } from "../common/Toolbar/ActionReportToolbar";
import { useEffect, useMemo, useState, useCallback, useRef } from "react";
import { useAppDispatch, useAppSelector } from "../../../store";
import { useMaxUiKpis } from "../common/hooks/useMaxUiKpis";
import { setAffAbosToExpand, setAvailableKpis, toggleSortDirection } from "../../../reducers/whosNewArReport";
import { ROUTE_PATHS } from "../../../common/enums/routing-enums";
import { FetchWhosNewActionReportData } from "./WhosNewActionReportDataLayer";
import { filterKpisByConfiguration } from "./WhosNewActionReport";
import { WhosNewReportRecord } from "../../../services/ActionReports/ActionReportAPI.types";
import { getCurrentPeriod, getPreviousNPeriods } from "../../../common/util/period";
import { store as accBootStore } from "../../Lit/acc-boot/acc-boot.js";
import { ExportAffABO } from "../../../services/ExportV2/exportV2API.types";
import { MODULE_WHOS_NEW_V2 } from "../../../common/constants/export-constants";
import ServerSideExportInProgressNotify from "../../ServerSideExport/ServerSideExportCommon/ServerSideExportNotify/ServerSideExportInProgressNotify/ServerSideExportInProgressNotify";
import ServerSideExportCompletedNotify from "../../ServerSideExport/ServerSideExportCommon/ServerSideExportNotify/ServerSideExportCompletedNotify/ServerSideExportCompletedNotify";
import { dispatchOpenExportV2ModalEvent } from "../../ServerSideExport/ServerSideExportCommon/ServerSideExportUtil/ServerSideExportEvents";
import { buildFieldTitleOverrides } from "../../../common/util/exportUtil";
import { EXPORT_FIELD_ID_BY_KPI } from "../../../common/constants/export-constants";
import { usePpvRequirementType } from "../hooks/usePpvRequirementType";
import commonContent from "../../../content/common";
import { getCanonicalTitle } from "../../../common/util/Incentives/IncentiveRegistry";
import { BonusInfoFooter } from "../../../common/components/BonusInfoFooter/BonusInfoFooter";
import type {
  SearchSelectItem,
  SearchCallback,
  SelectionCallback,
} from "../../../common/components/SearchSelect/SearchSelect";
import { GroupLeaderFilter } from "../common/GroupLeaderFilter/GroupLeaderFilter";
import { GroupLeaderFilterErrorBoundary } from "../common/GroupLeaderFilter/GroupLeaderFilterErrorBoundary";
import { useGroupLeaderUrlSync } from "../../../common/hooks/useGroupLeaderUrlSync";
import { setSelectedLeader } from "../../../reducers/groupLeaderFilter";
import { leaderExistsInTree, parentLeaderOf } from "../common/groupLeaderGrouping";
import type { RecordRef } from "../../../services/ActionReports/ActionReportAPI.types";
import { useGetSavedKpiPreferenceQuery } from "../../../services/KPIPreferences/savedKpiPreferencesAPI";
import {
  whosNewPageViewTrack,
  whosNewTreeViewTrack,
  whosNewListViewTrack,
  whosNewExportTrack,
  whosNewCustomizeKpiTrack,
  whosNewApplyCustomizeKpiTrack,
  whosNewSelectGroupLeaderTrack,
  whosNewSortAscendingTrack,
  whosNewSortDescendingTrack,
  whosNewSearchTrack,
} from "../../../common/tealium/ActionReports/WhosNewTrack";
import { WhosNewMonthTabButtons } from "./WhosNewMonthTabs";
import { getCountryNameFromCode } from "../../../common/util/formatCountryName";
import { storedLocale } from "../../Authentication/util";
import maskAboNumber from "../../../common/util/maskAboNumber";
import { getWhosNewDisplayName } from "./whosNewCustomerUtils";

const padAffSafe = (aff: string): string => {
  if (aff.length >= 3) return aff;
  return /^\d+$/.test(aff) ? aff.padStart(3, "0") : aff;
};

// Default KPIs shown when user has no saved preferences
const DEFAULT_KPIS = ["ppvRequirement", "groupPV", "sponsoring", "totalABOCount", "customers", "autoRenewal"];

const PERIOD_COUNT = 12;

const formatAffAbo = (aff: string, abo: string): string => `${aff.padStart(3, "0")}-${abo}`;

const extractExportAffAbos = (records: WhosNewReportRecord[]): ExportAffABO[] => {
  return records.map((record) => ({
    affAbo: formatAffAbo(record.Aff, record.Abo),
    level: record.Level,
    sponsorAbo: "",
    businessNature: record.BusinessNature,
  }));
};

const useSelectedKpis = (availableKpis: string[] | null | undefined) => {
  const { savedKpiPreferencesData } = useAppSelector((state) => state.savedKpiPreferencesData);
  useGetSavedKpiPreferenceQuery("whosNewKPIs", { refetchOnMountOrArgChange: true });

  return useMemo(() => {
    const savedKpis = savedKpiPreferencesData?.savedDataViews?.whosNewKPIs?.availableKpis;
    const selectedKpis = savedKpis ? savedKpis.filter((kpi) => kpi.displaydefault).map((kpi) => kpi.id) : DEFAULT_KPIS;
    return filterKpisByConfiguration(selectedKpis, availableKpis);
  }, [savedKpiPreferencesData, availableKpis]);
};

type TreeViewControlsProps = {
  isTreeView: boolean;
  treeTabSelected: number;
  onTabSelect: (months: number) => void;
};

function TreeViewControls({ isTreeView, treeTabSelected, onTabSelect }: TreeViewControlsProps) {
  const tabsRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const el = tabsRef.current;
    if (!el) return;
    const onWheel = (e: WheelEvent) => {
      if (e.deltaY === 0) return;
      e.preventDefault();
      el.scrollLeft += e.deltaY;
    };
    el.addEventListener("wheel", onWheel, { passive: false });
    return () => el.removeEventListener("wheel", onWheel);
  }, []);

  return (
    <div className="whos-new-layout__treeViewControls">
      <div
        ref={tabsRef}
        role="tablist"
        className={`whos-new-layout__treeViewTabs${!isTreeView ? " whos-new-layout__treeViewTabs--hidden" : ""}`}
      >
        <WhosNewMonthTabButtons treeTabSelected={treeTabSelected} onTabSelect={onTabSelect} />
      </div>
    </div>
  );
}

export function WhosNewActionReportLayout() {
  const { t } = useTranslation(["actionReportsWhosNew", "leafAboName", "navigation", "common"]);
  const maxUiKpis = useMaxUiKpis();
  const location = useLocation();
  const isTreeView = location.pathname.includes("/tree");
  const [treeTabSelected, setTreeViewTabSelected] = useState(1);
  const currentPeriod = getCurrentPeriod();
  const { availableKpis, sortDirection } = useAppSelector((state) => state.whosNewActionReportTracker);
  const { configuration } = useAppSelector(({ boot }) => boot);
  const { uiKpis } = configuration;
  const showWhosNewIncludesCustomers = configuration.actionReports.showWhosNewIncludesCustomers;
  const enableGroupLeaderFilter = configuration.actionReports.enableGroupLeaderFilter;
  const symbol = String(t("disclaimerSymbol", { defaultValue: commonContent.disclaimerSymbol, ns: "common" }));
  const pageTitle = getCanonicalTitle({ id: showWhosNewIncludesCustomers ? "whosNewWithCustomers" : "whosNew" }, t, {
    symbol,
  });
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { whosNewActionReportData, isLoading } = FetchWhosNewActionReportData(currentPeriod);
  const { ppvTitleKey } = usePpvRequirementType(currentPeriod);

  const isMaskAboNumber = useAppSelector((s) => s.boot.configuration.actionReports.maskConfidentialAboNumber) ?? false;

  const groupLeaders = useMemo(
    () => whosNewActionReportData.GroupLeaders ?? [],
    [whosNewActionReportData.GroupLeaders],
  );
  const groupLeaderTree = useMemo(
    () => whosNewActionReportData.GroupLeaderTree ?? [],
    [whosNewActionReportData.GroupLeaderTree],
  );
  // Pass the raw (possibly undefined) field so the hook defers URL hydration
  // until the leader list has actually loaded — otherwise a deep-link `?gl=`
  // can be cleared against the pre-fetch empty default.
  useGroupLeaderUrlSync(isLoading ? undefined : whosNewActionReportData.GroupLeaders);
  const confidentialUser = t("ConfidentialUser", "Confidential User", { ns: "leafAboName" });
  const newCustomerLabel = t("newCustomer", defaultContent["newCustomer"] as string, {
    ns: "actionReportsWhosNew",
  });

  // Search state
  const [searchText, setSearchText] = useState<string>("");
  const [scrollToAbo, setScrollToAbo] = useState<{ aff: string; abo: string; period: string } | undefined>(undefined);

  const ppvRequirementTitle = t(ppvTitleKey, commonContent[ppvTitleKey], { ns: "common" });
  const customerAboNumberTitle = t("customerAboNumber", defaultContent.customerAboNumber as string);

  const selectedKpis = useSelectedKpis(uiKpis.whosNewV2);

  const tableColumns = t("table.columns", {
    ns: "actionReportsWhosNew",
    returnObjects: true,
    defaultValue: (defaultContent.table as { columns: { [key: string]: string } }).columns,
  }) as { [key: string]: string };

  const fieldTitleOverrides = useMemo(() => {
    const overrides = buildFieldTitleOverrides(tableColumns);
    // Include the dynamic ppvRequirement title (PPV / VCS PV / PCPV depending on market)
    const ppvExportFieldId = EXPORT_FIELD_ID_BY_KPI["ppvRequirement"];
    if (ppvExportFieldId) {
      overrides[ppvExportFieldId] = ppvRequirementTitle;
    }

    if (showWhosNewIncludesCustomers) {
      overrides["aboNo"] = customerAboNumberTitle;
    }
    return overrides;
  }, [tableColumns, ppvRequirementTitle, showWhosNewIncludesCustomers, customerAboNumberTitle]);

  const filteredDefaultKpis = useMemo(() => {
    const kpis = uiKpis.whosNewV2;
    if (kpis === null || kpis === undefined) {
      return DEFAULT_KPIS;
    }
    return DEFAULT_KPIS.filter((kpi) => kpis.includes(kpi));
  }, [uiKpis.whosNewV2]);

  const extractAbos = useCallback(
    (periodCount = PERIOD_COUNT) => {
      const periods = getPreviousNPeriods(currentPeriod, periodCount, true);
      return whosNewActionReportData.Whosnew.filter((abo) => periods.includes(abo.ConversionPeriod));
    },
    [whosNewActionReportData.Whosnew, currentPeriod],
  );

  useEffect(() => {
    whosNewPageViewTrack(currentPeriod);
  }, [currentPeriod]);

  useEffect(() => {
    dispatch(setAvailableKpis(uiKpis.whosNewV2));
  }, [dispatch, uiKpis.whosNewV2]);

  useEffect(() => {
    if (ppvTitleKey && ppvTitleKey !== "ppv") {
      dispatch(setAvailableKpis([...availableKpis, "ppv"]));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, ppvTitleKey]);

  const filteredData = extractAbos();

  const getWhosNewName = useCallback(
    (record: WhosNewReportRecord) =>
      getWhosNewDisplayName(record, {
        confidentialUser,
        newCustomerLabel,
        showCustomerDetails: showWhosNewIncludesCustomers,
      }),
    [confidentialUser, newCustomerLabel, showWhosNewIncludesCustomers],
  );

  // Search pool always uses the full record set so users can find any ABO
  // regardless of which group leader is currently selected. Selection
  // auto-switches the leader filter to the found ABO's leader.
  const searchPool = useMemo(() => {
    return whosNewActionReportData.Whosnew.map((record) => ({
      aff: record.Aff,
      abo: record.Abo,
      aboName: getWhosNewName(record),
      isoCountry: record.IsoCountry,
      period: record.ConversionPeriod,
      firstGroupLeader: record.FirstGroupLeader,
      isConfidential: record.ConfidentialUser,
    }));
  }, [whosNewActionReportData.Whosnew, getWhosNewName]);

  // Search callbacks
  const handleSearchCallback = useCallback<SearchCallback>(
    (text) => {
      return searchPool
        .filter((entry) => entry.aboName.toLowerCase().includes(text.toLowerCase()) || entry.abo.includes(text))
        .map((entry): SearchSelectItem => {
          const sublabel = `${isMaskAboNumber && entry.isConfidential ? maskAboNumber(entry.abo) : entry.abo} | ${getCountryNameFromCode(entry.isoCountry, storedLocale())}`;
          return {
            key: `${entry.aff}-${entry.abo}-${entry.period}`,
            label: entry.aboName,
            sublabel,
          };
        });
    },
    [searchPool, isMaskAboNumber],
  );

  const handleSelectionCallback = useCallback<SelectionCallback>(
    (key) => {
      const found = searchPool.find((entry) => `${entry.aff}-${entry.abo}-${entry.period}` === key);
      if (found) {
        if (isTreeView) {
          document.dispatchEvent(
            new CustomEvent("acc-los-data-find", { detail: { record: { aboNumber: found.abo } } }),
          );
        } else {
          const selfRef: RecordRef = { aff: found.aff, abo: found.abo };
          const isInTree = leaderExistsInTree(groupLeaderTree, selfRef);
          const fglMatchesSelf =
            !!found.firstGroupLeader &&
            padAffSafe(found.firstGroupLeader.aff) === padAffSafe(found.aff) &&
            found.firstGroupLeader.abo === found.abo;
          const isSelfLeader = isInTree || fglMatchesSelf;

          let targetLeader: RecordRef | null = null;
          if (groupLeaders.length > 0) {
            if (isSelfLeader) {
              targetLeader = parentLeaderOf(groupLeaderTree, selfRef);
            } else if (found.firstGroupLeader?.aff || found.firstGroupLeader?.abo) {
              targetLeader = found.firstGroupLeader;
            }
          }

          if (groupLeaders.length > 0) {
            dispatch(setSelectedLeader(targetLeader));
          }

          setScrollToAbo({ aff: found.aff, abo: found.abo, period: found.period });
        }
        setSearchText(found.aboName);
      }
      return { displayName: found?.aboName ?? "" };
    },
    [searchPool, isTreeView, groupLeaderTree, groupLeaders, dispatch],
  );

  const handleClearSearch = useCallback(() => {
    setSearchText("");
    setScrollToAbo(undefined);
  }, []);

  // Sort handler
  const handleSortClick = useCallback(() => {
    if (sortDirection === "desc") {
      whosNewSortAscendingTrack();
    } else {
      whosNewSortDescendingTrack();
    }
    // Don't scroll to top if a table row scroll is in progress
    if (!window.__tableRowScrollInProgress) {
      window.scrollTo({ top: 0, behavior: "instant" });
    }
    dispatch(toggleSortDirection());
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        window.dispatchEvent(new Event("scroll"));
      });
    });
  }, [dispatch, sortDirection]);

  // The toolbar's left button highlights on "asc" and right on "desc", but for
  // this report "desc" means newest-first (left = default). Invert so the
  // button highlight matches the visual convention without changing data logic.
  const toolbarSortDirection = sortDirection === "desc" ? "asc" : "desc";

  const exportAffAbos = useMemo(() => {
    return extractExportAffAbos(filteredData);
  }, [filteredData]);

  const notifyDownloadLinkOnClick = () => {
    dispatchOpenExportV2ModalEvent(MODULE_WHOS_NEW_V2, true);
  };

  useEffect(() => {
    return () => {
      dispatch(setAffAbosToExpand([]));
      accBootStore.setState({ treeView: { affAbosToExpand: [] } });
    };
  }, [dispatch]);

  const prevIsTreeView = useRef(isTreeView);
  useEffect(() => {
    if (isTreeView && !isLoading) {
      const affAbosToExpand = extractAbos(treeTabSelected).map((abo) => formatAffAbo(abo.Aff, abo.Abo));
      dispatch(setAffAbosToExpand(affAbosToExpand));
      accBootStore.setState({ treeView: { affAbosToExpand } });
    } else if (!isTreeView && prevIsTreeView.current) {
      dispatch(setAffAbosToExpand([]));
      accBootStore.setState({ treeView: { affAbosToExpand: [] } });
    }
    prevIsTreeView.current = isTreeView;
  }, [dispatch, extractAbos, isTreeView, isLoading, treeTabSelected]);

  const handleTreeViewToggle = useCallback(() => {
    if (!isTreeView) {
      whosNewTreeViewTrack();
      navigate(`${ROUTE_PATHS.WHOS_NEW_ACTION_REPORT}/tree`);
    } else {
      whosNewListViewTrack();
      navigate(ROUTE_PATHS.WHOS_NEW_ACTION_REPORT);
    }
  }, [isTreeView, navigate]);

  return (
    <main className={`whos-new-layout${isTreeView ? " whos-new-layout--treeView" : ""}`}>
      <ActionReportHeader
        title={pageTitle}
        description={
          showWhosNewIncludesCustomers
            ? `${t("title.pageDescriptionWithCustomers", (defaultContent.title as Record<string, string>).pageDescriptionWithCustomers)}`
            : `${t("title.pageDescription", (defaultContent.title as Record<string, string>).pageDescription)}`
        }
        reportLength={filteredData.length}
      />
      <ActionReportToolbar
        reportLength={filteredData.length}
        isAllEnriched={true}
        treeViewToggle={{
          enabled: !configuration.actionReports.disableTreeView,
          isTreeView: isTreeView,
          onToggle: handleTreeViewToggle,
          disabled: isLoading || filteredData.length === 0,
          label: t("losTreeView", defaultContent.losTreeView as string).toString(),
        }}
        sort={{
          enabled: true,
          direction: toolbarSortDirection,
          onClick: handleSortClick,
          disabled: isLoading || filteredData.length === 0,
        }}
        kpiSelector={{
          subHeader: `${t("kpiSelectorDescription", defaultContent.kpiSelectorDescription as string)}`,
          availableKpis: availableKpis,
          defaultKpis: filteredDefaultKpis,
          saveViewName: "whosNewKPIs",
          saveViewDescription: "Whos New KPI Preferences",
          maxSelectionsAllowed: maxUiKpis,
          textOverrides: {
            ppvRequirement: ppvRequirementTitle,
          },
          disabled: isLoading || filteredData.length === 0,
          onOpenTrack: whosNewCustomizeKpiTrack,
          onApplyTrack: whosNewApplyCustomizeKpiTrack,
        }}
        search={{
          enabled: true,
          searchCallback: handleSearchCallback,
          selectionCallback: handleSelectionCallback,
          searchText: searchText,
          clearFunction: handleClearSearch,
          disabled: isLoading || searchPool.length === 0,
          onFocus: whosNewSearchTrack,
        }}
        export={{
          moduleId: MODULE_WHOS_NEW_V2,
          exportAffAbos,
          selectedKpis,
          confirmationMessage: t(
            "exportConfirmation",
            "This report will include the last twelve months of who's new.",
            {
              ns: "actionReportsWhosNew",
            },
          ).toString(),
          fieldTitleOverrides,
        }}
        exportTrack={whosNewExportTrack}
        groupLeaderFilterSlot={
          enableGroupLeaderFilter ? (
            isLoading ? (
              <GroupLeaderFilter leaders={[]} tree={[]} isLoading />
            ) : groupLeaders.length > 0 ? (
              <GroupLeaderFilterErrorBoundary>
                <GroupLeaderFilter
                  leaders={groupLeaders}
                  tree={groupLeaderTree}
                  onSelectTrack={whosNewSelectGroupLeaderTrack}
                />
              </GroupLeaderFilterErrorBoundary>
            ) : (
              <GroupLeaderFilter leaders={[]} tree={[]} isLoading={false} />
            )
          ) : undefined
        }
      />
      {isTreeView && (
        <TreeViewControls
          isTreeView={isTreeView}
          treeTabSelected={treeTabSelected}
          onTabSelect={setTreeViewTabSelected}
        />
      )}
      <div className={`content-container ${isTreeView ? "tree-view" : ""}`}>
        <Outlet
          context={{
            ppvTitleKey,
            sortDirection,
            scrollToAbo,
            showWhosNewIncludesCustomers,
            treeTabSelected,
            onTreeTabSelect: setTreeViewTabSelected,
          }}
        />
      </div>
      <div>
        <BonusInfoFooter />
      </div>
      <ServerSideExportInProgressNotify />
      <ServerSideExportCompletedNotify
        moduleId={MODULE_WHOS_NEW_V2}
        notifyDownloadLinkOnClick={notifyDownloadLinkOnClick}
      />
    </main>
  );
}
